'use client';

import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import type React from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import logoWhite from '@/app/assets/imgs/logo-white.png';
import { App } from '@/app/components/icons/App';
import { ClientLink } from '@/app/components/shared/ClientLink';
import type { RegistrationConfig } from '@/app/components/shared/Footer/types';
import { getRegistrationConfig, getTermsAndPrivacy } from '@/app/components/shared/Footer/utils';
import { ProductSteps } from '@/app/components/shared/ProductSteps';
import { useToast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import { API_ACCESS_DOC_URL } from '../_constants/features';
import { HERO_BUBBLE_AVATARS, HERO_VIDEO_SOURCES_EN, HERO_VIDEO_SOURCES_ZH, STEP_IMAGES } from '../_constants/media';
import { BannerRoleSection } from './BannerRoleSection';
import { FeatureGrid } from './FeatureGrid';
import { HeroVideoCarousel } from './HeroVideoCarousel';
import { QuestionSection } from './QuestionSection';
import { UseCaseSection } from './UseCaseSection';

// header 滚动后的渐进式模糊：顶部最强（40px）向下递减至 0，各层用 mask 只保留对应竖直区间。
const HEADER_BLUR_LAYERS: { blur: number; mask: string }[] = [
  { blur: 0.5, mask: 'linear-gradient(black 0%, black 87.5%, transparent 100%)' },
  { blur: 1, mask: 'linear-gradient(black 0%, black 75%, transparent 87.5%)' },
  { blur: 2, mask: 'linear-gradient(black 0%, black 62.5%, transparent 75%)' },
  { blur: 4, mask: 'linear-gradient(black 0%, black 50%, transparent 62.5%)' },
  { blur: 8, mask: 'linear-gradient(black 0%, black 37.5%, transparent 50%)' },
  { blur: 16, mask: 'linear-gradient(black 0%, black 25%, transparent 37.5%)' },
  { blur: 28, mask: 'linear-gradient(black 0%, black 12.5%, transparent 25%)' },
  { blur: 40, mask: 'linear-gradient(black 0%, transparent 12.5%)' },
];

export const ViduStream: React.FC = () => {
  const t = useTranslations();
  const locale = useLocale();
  const heroVideoSources = locale === 'zh' ? HERO_VIDEO_SOURCES_ZH : HERO_VIDEO_SOURCES_EN;
  // 气泡文案随语言翻译，顺序与视频数组一致。
  const heroBubbleTexts = t.raw('vidu-stream.heroBubbles') as string[];
  const { toast } = useToast();
  const { exitNotice, clearLiveExitNotice } = useStore((s) => ({
    exitNotice: s.exitNotice,
    clearLiveExitNotice: s.clearLiveExitNotice,
  }));
  const { open: openDownloadAppDialog } = useGlobalModal('downloadAppDialog');
  const [activeHeroIndex, setActiveHeroIndex] = useState(0);
  // 当前视频时长（ms），用于让弹幕滚动时长与视频一致。
  const [activeHeroDurationMs, setActiveHeroDurationMs] = useState(8000);
  // 页面滚动后给顶栏加渐变底，避免 logo/按钮与内容重叠时看不清。
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const registrationConfig = getRegistrationConfig();
  const termsAndPrivacy = getTermsAndPrivacy();

  const renderComplianceLink = (item: RegistrationConfig, index: number, length: number) => (
    <Fragment key={item.id}>
      <span className="inline-flex items-center">
        {item.href ? (
          <ClientLink
            href={item.href}
            target={item.target || '_self'}
            rel={item.rel || undefined}
            className="inline-flex items-center gap-1 text-white/64 transition hover:text-white"
          >
            {item.icon ? <img src={item.icon} alt={item.alt} className="h-4 w-4 shrink-0" /> : null}
            <span>{item.text}</span>
          </ClientLink>
        ) : (
          <span className="inline-flex items-center gap-1 text-white/64">
            {item.icon ? <img src={item.icon} alt={item.alt} className="h-4 w-4 shrink-0" /> : null}
            <span>{item.text}</span>
          </span>
        )}
      </span>
      {index < length - 1 ? <span className="text-white/20">|</span> : null}
    </Fragment>
  );
  const hasReportedPvRef = useRef(false);
  useEffect(() => {
    if (hasReportedPvRef.current) return;
    hasReportedPvRef.current = true;
    reportEvent('page_view', {
      page_name: 'stream_page',
    });
  }, []);

  // 页面滚动后给顶栏加黑色渐变背景（黑 24% → 黑 0%），保证 logo/按钮可读。
  useEffect(() => {
    const onScroll = () => setIsHeaderScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!exitNotice) {
      return;
    }

    toast({
      type: 'warning',
      title: exitNotice.message || t(`live.exitNotice.${exitNotice.reason}`),
    });
    clearLiveExitNotice();
  }, [clearLiveExitNotice, exitNotice, toast]);

  // 赚钱步骤数据。配图按显示顺序取 STEP_IMAGES[0..2]。
  const stepItems = [
    {
      index: 1,
      title: t('vidu-stream.steps.step2.title'),
      description: t('vidu-stream.steps.step2.description'),
      image: STEP_IMAGES[0],
    },
    {
      index: 2,
      title: t('vidu-stream.steps.step3.title'),
      description: t('vidu-stream.steps.step3.description'),
      image: STEP_IMAGES[1],
    },
    {
      index: 3,
      title: t('vidu-stream.steps.step4.title'),
      description: t('vidu-stream.steps.step4.description'),
      image: STEP_IMAGES[2],
    },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-black text-white">
      <div
        className={`pointer-events-none fixed inset-x-0 top-0 z-20 transition-[background] duration-300 ${
          isHeaderScrolled ? 'bg-[linear-gradient(180deg,rgba(0,0,0,0.4)0%,rgba(0,0,0,0)100%)]' : ''
        }`}
      >
        {/* 滚动后：自上而下渐进式模糊（顶部 40px → 底部 0），层叠遮罩实现平滑过渡 */}
        {isHeaderScrolled
          ? HEADER_BLUR_LAYERS.map((layer) => (
              <div
                key={layer.blur}
                className="pointer-events-none absolute inset-0"
                style={{ backdropFilter: `blur(${layer.blur}px)`, maskImage: layer.mask, WebkitMaskImage: layer.mask }}
              />
            ))
          : null}
        <div className="relative mx-auto flex w-full max-w-[1440px] items-center justify-between px-16 pt-6 pb-4 sm:px-12 sm:pt-4">
          <ClientLink
            href="/"
            aria-label={t('sidebar.home')}
            className="pointer-events-auto relative h-[88px] w-[82px] sm:h-[88px] sm:w-[72px]"
          >
            <Image src={logoWhite} alt="Vidu" fill sizes="82px" className="object-contain" priority />
          </ClientLink>
          <div className="pointer-events-auto flex items-center gap-3">
            <a
              href={
                SITE === 'china'
                  ? 'https://shengshu.feishu.cn/wiki/PSlDwao9GiEGOKkWS2FcWRtOnwe'
                  : 'https://auspicious-passive-36a.notion.site/Vidu-S1-Introduction-34324005a3e48009b3f3d9c07c79d83b'
              }
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 items-center rounded-full border border-system-white40 bg-system-white16 px-7 font-medium text-base text-white backdrop-blur-[40px] transition-colors hover:border-white hover:bg-system-white24 sm:h-9 sm:px-4 sm:text-sm"
            >
              {t('vidu-stream.instructions')}
            </a>
            <a
              href={
                SITE === 'china' ? 'https://platform.vidu.cn/docs/vidu-s1' : 'https://platform.vidu.com/docs/vidu-s1'
              }
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 items-center rounded-full border border-system-white40 bg-system-white16 px-7 font-medium text-base text-white backdrop-blur-[40px] transition-colors hover:border-white hover:bg-system-white24 sm:h-9 sm:px-4 sm:text-sm"
            >
              {t('vidu-stream.buttons.apiPlatform')}
            </a>
          </div>
        </div>
      </div>

      {/* Hero Section：上方视频区，下方 banner 区上移与视频交叠 */}
      <section className="relative flex w-full flex-col">
        {/* 视频区外层：与 banner 同宽规则（max-w-1440 + px-8），视频落在带圆角的框内 */}
        <div className="relative z-0 mx-auto w-full max-w-[1440px] px-8 py-8 sm:px-4 sm:py-4">
          <div className="relative h-[60vh] w-full overflow-hidden rounded-[24px] border border-white/[0.16]">
            {/* 背景轮播视频 */}
            <div className="absolute inset-0">
              <HeroVideoCarousel
                sources={heroVideoSources}
                onActiveIndexChange={setActiveHeroIndex}
                onActiveDurationChange={setActiveHeroDurationMs}
              />
              {/* 暗化遮罩，保证文字与输入框可读 */}
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.48)0%,rgba(0,0,0,0.20)45%,rgba(0,0,0,0.72)100%)]" />
            </div>

            {/* hero 气泡（桌面）：左下角绝对定位；移动端隐藏，改由标题上方文档流渲染 */}
            {heroBubbleTexts[activeHeroIndex] ? (
              <div className="pointer-events-none absolute bottom-8 left-8 z-10 sm:hidden">
                <span
                  key={activeHeroIndex}
                  className="hero-float-pass inline-flex max-w-[70vw] items-center gap-3 whitespace-nowrap rounded-full bg-white/[0.22] py-2 pr-7 pl-2.5 font-medium text-[19px] text-white backdrop-blur-[20px]"
                  style={{ animationDuration: `${activeHeroDurationMs}ms` }}
                >
                  <img
                    src={HERO_BUBBLE_AVATARS[activeHeroIndex % HERO_BUBBLE_AVATARS.length]}
                    alt=""
                    className="size-8 shrink-0 rounded-full object-cover"
                  />
                  <span>{heroBubbleTexts[activeHeroIndex]}</span>
                </span>
              </div>
            ) : null}

            {/* 前景内容：底部距视频底部 8% */}
            <div className="absolute inset-x-0 bottom-[6%] z-10 flex flex-col items-center px-4">
              {/* hero 气泡（移动端）：标题上方文档流，永不交叠 */}
              {heroBubbleTexts[activeHeroIndex] ? (
                <span
                  key={activeHeroIndex}
                  className="hero-float-pass mb-4 hidden max-w-[80vw] items-center gap-3 whitespace-nowrap rounded-full bg-white/[0.22] py-1.5 pr-6 pl-2 font-medium text-[16px] text-white backdrop-blur-[20px] sm:inline-flex"
                  style={{ animationDuration: `${activeHeroDurationMs}ms` }}
                >
                  <img
                    src={HERO_BUBBLE_AVATARS[activeHeroIndex % HERO_BUBBLE_AVATARS.length]}
                    alt=""
                    className="size-7 shrink-0 rounded-full object-cover"
                  />
                  <span>{heroBubbleTexts[activeHeroIndex]}</span>
                </span>
              ) : null}
              <div className="w-full text-center">
                <h1 className="font-medium text-[48px] text-white/88 leading-tight sm:text-[32px]">
                  {t('vidu-stream.title')}
                </h1>
                <p className="mt-2 font-normal text-[18px] text-white/40 sm:text-[14px]">{t('vidu-stream.subtitle')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* 角色区：第一格用户角色（有）/ 创建卡（无），后接官方角色。 登录/consent/claim/创建/分享等交互连同弹层已内聚到该组件。 */}
        <BannerRoleSection />
      </section>

      {/* Vidu S1 应用案例 */}
      <UseCaseSection />

      {/* 赚钱步骤区 */}
      <ProductSteps
        title={t('vidu-stream.steps.title')}
        items={stepItems}
        page="vidu-stream"
        theme="dark"
        columns={3}
        variant="plain"
        showButton={false}
        className="pt-[88px] pb-16"
        contentClassName="px-4 py-0 md:px-2 md:py-0"
        headerExtra={
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href={API_ACCESS_DOC_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full border border-white/40 px-8 py-3 font-medium text-base text-white transition-colors hover:border-white hover:bg-white hover:text-black"
            >
              {t('vidu-stream.buttons.apiAccess')}
            </a>
            <button
              type="button"
              onClick={() => {
                openDownloadAppDialog({ fromPage: 'landing_page' });
                reportEvent('get_app_click', { click_position: 'vidu_stream' });
              }}
              className="inline-flex cursor-pointer items-center justify-center rounded-full border border-white/40 px-8 py-3 font-medium text-base text-white transition-colors hover:border-white hover:bg-white hover:text-black"
            >
              <App className="mr-1 flex-shrink-0 text-lg" />
              {t('vidu-stream.buttons.downloadApp')}
            </button>
          </div>
        }
      />

      {/* 功能卡片网格 */}
      <FeatureGrid />

      {/* FAQ Section */}
      <QuestionSection />

      <section className="border-white/10 border-t px-6 py-6 sm:px-4 sm:py-5">
        <div className="mx-auto flex w-full max-w-[1280px] flex-wrap items-center justify-center gap-x-6 gap-y-3 text-center text-sm leading-6 sm:text-xs sm:leading-5">
          {SITE === 'china' ? (
            <>
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
                {registrationConfig.map((item, index) => renderComplianceLink(item, index, registrationConfig.length))}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
                {termsAndPrivacy.map((item, index) => renderComplianceLink(item, index, termsAndPrivacy.length))}
              </div>
            </>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
              {termsAndPrivacy.map((item, index) => renderComplianceLink(item, index, termsAndPrivacy.length))}
            </div>
          )}
          <p className="w-full text-center text-white/40 text-xs leading-5 sm:text-[11px]">
            {t('vidu-stream.compliance.description')}
          </p>
        </div>
      </section>
    </div>
  );
};
